import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSteps from '@/components/ProcessSteps';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Shield, FileText, CheckCircle, Users } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function TrademarkOwnershipTransfers() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    {
      icon: Shield,
      title: 'Legal Compliance',
      description: 'Ensure all ownership transfers meet USPTO requirements and regulations.',
    },
    {
      icon: FileText,
      title: 'Proper Documentation',
      description: 'Complete all necessary paperwork for seamless ownership transfer.',
    },
    {
      icon: CheckCircle,
      title: 'Verified Process',
      description: 'Professional handling of assignment agreements and recordation.',
    },
    {
      icon: Users,
      title: 'Expert Guidance',
      description: 'Experienced team to navigate complex transfer procedures.',
    },
  ];

  const faqs = [
    {
      question: 'What is a trademark ownership transfer?',
      answer: 'A trademark ownership transfer, also known as an assignment, is the legal process of transferring ownership rights of a registered trademark from one party to another.',
    },
    {
      question: 'When is a trademark transfer necessary?',
      answer: 'Transfers are necessary during business acquisitions, mergers, sales, restructuring, or when gifting or selling intellectual property rights to another party.',
    },
    {
      question: 'How long does the transfer process take?',
      answer: 'Once documents are submitted, the USPTO typically processes trademark assignments within 2-3 months, though this timeline can vary.',
    },
    {
      question: 'What documents are required?',
      answer: 'Key documents include a signed assignment agreement, proof of ownership, and proper recordation forms filed with the USPTO.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />

      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1554224311-beee460201f9?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>
              Trademark Ownership Transfers
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
              Seamlessly transfer trademark ownership with professional legal support and documentation.
            </p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>

      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our Transfer Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Professional trademark ownership transfer services you can trust.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center">
                      <Icon className="text-white" size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <ProcessSteps />

      {/* Pricing Section */}
      <PricingTable />

      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Get answers to common questions about trademark transfers.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <TestimonialSlider autoSlide={true} />

      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Ready to Transfer Ownership?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Let our experts handle your trademark transfer with precision and care.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>

      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="trademark_ownership_transfer" />
      <Footer />
    </div>
  );
}